import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { listMyOrganizations, type UserOrganization, type ErrorResponse } from '@vn-propane/shared';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { LanguageSelector } from '@/components/ui/language-selector';
import { User } from 'lucide-react';

export default function OrganizationsPage() {
  const { t } = useTranslation(['admin', 'common', 'errors']);
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<UserOrganization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchOrganizations() {
      try {
        const result = await listMyOrganizations();

        if (result.error) {
          const errorResponse = result.error as ErrorResponse;
          if (result.response?.status === 401) {
            navigate('/login');
            return;
          }
          setError(errorResponse.error?.message || t('organizations.failedToLoad'));
          return;
        }

        if (result.data) {
          setOrganizations(result.data.data);
        }
      } catch (err) {
        setError(t('errors:unexpectedError'));
      } finally {
        setIsLoading(false);
      }
    }

    fetchOrganizations();
  }, [navigate, t]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted/40 p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight mb-8">{t('organizations.title')}</h1>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-6 bg-muted rounded w-3/4" />
                  <div className="h-4 bg-muted rounded w-1/2 mt-2" />
                </CardHeader>
                <CardContent>
                  <div className="h-4 bg-muted rounded w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/40 p-4 md:p-8 relative">
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <LanguageSelector />
        <button
          onClick={() => navigate('/account')}
          className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          title={t('common:navigation.accountSettings')}
        >
          <User className="h-5 w-5" />
        </button>
      </div>
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div className="flex items-center gap-3 min-w-0">
            <img src={`${import.meta.env.BASE_URL}logo-mark-dark.png`} alt="" className="h-12 w-12 shrink-0" />
            <div className="min-w-0">
              <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight truncate">{t('organizations.title')}</h1>
              <p className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
                giao<span className="text-propane-500">gas</span>
              </p>
            </div>
          </div>
          <div className="flex gap-3 items-center">
            <Button onClick={() => navigate('/organizations/new')}>
              {t('organizations.createOrganization')}
            </Button>
            <Button variant="outline" onClick={() => navigate('/login')}>
              {t('common:buttons.logout')}
            </Button>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {organizations.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <p className="text-center text-muted-foreground">
                {t('organizations.noOrganizationsYet')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {organizations.map((org) => (
              <Card
                key={org.id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/organizations/${org.id}`)}
              >
                <CardHeader>
                  <CardTitle>{org.name}</CardTitle>
                  <CardDescription>
                    {t('organizations.role', { role: org.role })}
                  </CardDescription>
                </CardHeader>
                {org.description && (
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{org.description}</p>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
